import { useAppDispatch, useAppSelector } from "state/hooks.ts";
import { Contest, ContestEntry } from "interfaces/leaderboard/contest.ts";
import React, { useState } from "react";
import BlockyLightText from "components/custom-texts/blocky-light-text.tsx";
import { showDepositModalV2 } from "state/slices/deposit-funds-modal-v2-slice.ts";
import BlockyMediumText from "components/custom-texts/blocky-medium-text.tsx";
import LongButton from "components/common/button/long-button.tsx";
import { getEnterContestPath, integratedEnterContest } from "utils/backend-path-builders.ts";
import { postRequest } from "utils/httpClient.ts";
import {
  setContestCurrentEntry,
  setContestsStale,
  setNewContestEntryState,
  setUserContestsStale
} from "state/slices/contests-slice.ts";
import { PulseLoader } from "react-spinners";
import { ErrorResponseBody } from "interfaces/error-response-body.ts";
import { fetchRealWallet } from "state/slices/user-real-wallet-slice.ts";
import { ErrorCodes } from "interfaces/error-codes.ts";
import { getIntegratedAppState, IntegratedAppState } from "state/slices/integrated-app-slice.ts";
import { useLocation, useNavigate } from "react-router-dom";
import { CONTEST_ENTRY_ID_QUERY_PARAM } from "utils/constants.ts";
import { getGroupState } from "state/slices/group-slice.ts";
import { setWalletIsStale } from "state/slices/wallet-slice.ts";

interface Props {
  contest: Contest;
  setShowModal: (b: boolean) => void;
}

const ContestModalEnterContestButton = ({ contest, setShowModal }: Props) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [sendingEnterRequest, setSendingEnterRequest] = useState(false);

  const integratedAppState = useAppSelector(getIntegratedAppState);
  const group = useAppSelector(getGroupState);
  const groupId = group.id;
  const isSpeedIntegration = group.name === "Speed" && integratedAppState.isIntegratedApp;

  const [enterContestError, setEnterContestError] = useState<string>("");

  const onClick = async () => {
    setEnterContestError("");

    setSendingEnterRequest(true);
    try {
      const path = integratedAppState.state === IntegratedAppState.NOT_INTEGRATED ?
        getEnterContestPath() : integratedEnterContest();

      const contestEntry: ContestEntry = await postRequest(path, {
        body: {
          contest_id: contest.id,
          group_id: groupId,
          status: "joined"
        },
      });

      dispatch(setContestCurrentEntry({ contestId: contest.id, entryId: contestEntry.id }));

      dispatch(setUserContestsStale(true));
      dispatch(setContestsStale(true));
      dispatch(setNewContestEntryState({
        id: contestEntry.id,
        balanceHasBeenAnimated: false,
        bannerHasBeenShown: false,
      }));
      
      const url = new URL(window.location.href);
      url.searchParams.set(CONTEST_ENTRY_ID_QUERY_PARAM, contestEntry.id);
      
      // Use navigate to update URL without full page reload
      navigate(`${location.pathname}${url.search}`, { replace: true });
      
      setShowModal(false);
    } catch (error: any) {

      const errorResponse: ErrorResponseBody | undefined = error.response?.data;
      if (errorResponse) {
        const error_code = errorResponse.error_code;

        switch (error_code) {
          case ErrorCodes.InsufficientFunds:
            dispatch(showDepositModalV2());
            break;

          case ErrorCodes.InvalidGeolocation:
            setEnterContestError("Geolocation failed. Please see help section " +
              "for approved jurisdictions. If available, you may join a 'Free' contest instead. " +
              "If you are in an approved jurisdiction, try a mobile device with WiFi turned off");
            break;
          default:
            setEnterContestError("Something went wrong");
        }
      } else {
        setEnterContestError("Something went wrong");
      }
    } finally {
      setSendingEnterRequest(false);
      if (isSpeedIntegration) {
        dispatch(setWalletIsStale(true));
      } else if (integratedAppState.state === IntegratedAppState.NOT_INTEGRATED) {
        dispatch(fetchRealWallet());
      }
    }
  };

  const enterContestButtonText = "Enter";

  return (
    <>
      {<div>
        <LongButton onClick={onClick} disabled={sendingEnterRequest}>
          {!sendingEnterRequest ? enterContestButtonText :
            <div className={"d-flex justify-content-center"}>
              Running checks <PulseLoader style={{ marginLeft: "0.5rem" }} size={7} />
            </div>}
        </LongButton>

        {
          !enterContestError && <div className={"m-3 text-center text-white"} style={{ fontSize: "10" }}>
            <BlockyLightText>
              By clicking '{enterContestButtonText}', you agree to the contest terms and agree that the Entry Fee
              will be debited from your account
            </BlockyLightText>
          </div>
        }

        {
          enterContestError &&
          <div className={"m-3 text-center rs-text-red"}>
            <BlockyMediumText>
              {enterContestError}
            </BlockyMediumText>
          </div>
        }

      </div>
      }
    </>
  );
};

export default ContestModalEnterContestButton;